/**
 * PassportVersionHistory — drop-in widget for /pcf product-detail pages.
 * Lists the full snapshot history for a product and lets the user diff any
 * two versions with one click.
 *
 *   <PassportVersionHistory productId={product.id} />
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, GitBranch, X, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';

export default function PassportVersionHistory({ productId }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);

  const load = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const r = await api.get(`/pcf/${productId}/versions`);
      setVersions(r.data?.versions || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load versions');
    } finally { setLoading(false); }
  }, [productId]);
  useEffect(() => { load(); }, [load]);

  const openDiff = async (a, b) => {
    setDiffLoading(true);
    try {
      const r = await api.get(`/pcf/${productId}/versions/diff?version_a=${a}&version_b=${b}`);
      setDiff(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Diff failed');
    } finally { setDiffLoading(false); }
  };

  if (loading) {
    return <Card className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></Card>;
  }
  if (versions.length === 0) {
    return (
      <Card className="p-6 text-sm text-muted-foreground text-center" data-testid="pv-empty">
        No passport versions yet — approve this product&apos;s PCF to snapshot the first one.
      </Card>
    );
  }

  return (
    <Card className="p-5" data-testid="passport-version-history">
      <div className="flex items-center gap-2 mb-4">
        <GitBranch className="w-4 h-4 text-primary" />
        <h3 className="font-heading font-semibold">Passport version history</h3>
        <span className="text-xs text-muted-foreground ml-auto">{versions.length} snapshot{versions.length === 1 ? '' : 's'}</span>
      </div>

      <ol className="relative border-l border-border ml-2 space-y-4">
        {versions.map((v, idx) => {
          const isLatest = idx === 0;
          const prev = versions[idx + 1];
          return (
            <li key={v.id} className="pl-4 -ml-px" data-testid={`pv-row-${v.version_number}`}>
              <span className={`absolute -left-1.5 w-3 h-3 rounded-full border-2 border-background ${isLatest ? 'bg-emerald-500' : 'bg-muted-foreground/50'}`} />
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">v{v.version_number}</span>
                    {isLatest && <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-[10px] uppercase">Current</Badge>}
                    <Badge variant="outline" className="text-[10px] uppercase">{v.trigger}</Badge>
                    <span className={`text-[10px] uppercase tracking-wide ${v.status === 'active' ? 'text-emerald-600' : 'text-muted-foreground'}`}>{v.status}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {new Date(v.created_at).toLocaleString()} · {v.actor_email || v.actor_role}
                  </div>
                  {v.reason && <div className="text-xs mt-1 italic">&ldquo;{v.reason}&rdquo;</div>}
                  <div className="text-[10px] font-mono text-muted-foreground mt-1">
                    hash {String(v.data_hash || '').slice(0, 16)}…
                  </div>
                </div>
                {prev && (
                  <Button size="sm" variant="outline" onClick={() => openDiff(prev.version_number, v.version_number)}
                          data-testid={`pv-diff-btn-${v.version_number}`}>
                    Diff vs v{prev.version_number}
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {(diff || diffLoading) && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="pv-diff-modal">
          <Card className="w-full max-w-xl p-5 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h4 className="font-medium">Version diff</h4>
                {diff && (
                  <div className="text-xs text-muted-foreground">
                    v{diff.from_version} <ArrowRight className="w-3 h-3 inline mx-1" /> v{diff.to_version}
                  </div>
                )}
              </div>
              <Button variant="ghost" size="icon" onClick={() => setDiff(null)} data-testid="pv-diff-close">
                <X className="w-4 h-4" />
              </Button>
            </div>
            {diffLoading || !diff ? (
              <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
            ) : diff.changes?.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-6">No field-level differences.</div>
            ) : (
              <ul className="space-y-2">
                {diff.changes.map((c, i) => (
                  <li key={i} className="border border-border rounded-md p-2 text-sm" data-testid={`pv-diff-row-${c.field}`}>
                    <div className="font-medium">{c.field}</div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-[11px] bg-red-500/10 text-red-600 border border-red-500/30 rounded px-2 py-0.5 line-through">{String(c.old_value ?? 'null')}</span>
                      <ArrowRight className="w-3 h-3 text-muted-foreground" />
                      <span className="text-[11px] bg-emerald-500/10 text-emerald-600 border border-emerald-500/30 rounded px-2 py-0.5">{String(c.new_value ?? 'null')}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {diff && (
              <div className="text-[10px] font-mono text-muted-foreground mt-3 border-t border-border pt-2">
                {String(diff.from_hash).slice(0, 20)}… → {String(diff.to_hash).slice(0, 20)}…
              </div>
            )}
          </Card>
        </div>
      )}
    </Card>
  );
}
